'use client';

import { type RefObject, useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

interface UseDialogFocusOptions {
  open: boolean;
  /** 초기 포커스를 받을 컨테이너. tabIndex={-1}가 지정되어 있어야 한다. */
  containerRef: RefObject<HTMLElement | null>;
  /** 전달하면 열려 있는 동안 Escape 키로 호출된다. */
  onEscape?: () => void;
}

/**
 * ui-v3 Dialog를 쓸 수 없는 커스텀 모달/오버레이의 공통 포커스 관리.
 *
 * - 열릴 때: 이전 포커스를 기억하고 컨테이너로 초기 포커스 이동
 * - 닫힐 때: 기억해 둔 이전 포커스로 복원 (preventScroll)
 * - 열려 있는 동안: Tab/Shift+Tab 순환 트랩 + (옵션) Escape 닫기
 */
export function useDialogFocus({
  open,
  containerRef,
  onEscape,
}: UseDialogFocusOptions) {
  useEffect(() => {
    if (!open) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    container.focus({ preventScroll: true });

    return () => {
      previousFocus?.focus({ preventScroll: true });
    };
  }, [open, containerRef]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onEscape?.();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      // offsetParent 체크로 display:none 요소(예: 데스크톱에서 숨긴 모바일 CTA) 제외
      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((element) => element.offsetParent !== null);

      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      const isActiveInside =
        active instanceof HTMLElement && container.contains(active);

      if (!isActiveInside) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey) {
        if (active === first || active === container) {
          event.preventDefault();
          last.focus();
        }
        return;
      }

      if (active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, containerRef, onEscape]);
}
